import React, { useState, useEffect } from 'react';
import { Sparkles, Zap, Gift, Star, ArrowRight, Loader2 } from 'lucide-react';
import { getPromotions } from '../services/api';
import { Button } from './ui/button';

const iconMap = {
  'Novos Clientes': Gift,
  'Lipozero': Zap,
  'Depilação a Laser': Sparkles,
  'Combo Especial': Star,
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromotions = async () => {
      const data = await getPromotions();
      setPromotions(data);
      setLoading(false);
    };
    fetchPromotions();
  }, []);

  const scrollToBooking = () => {
    document.querySelector('#marcacoes')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <section id="promocoes" className="py-24 bg-gradient-to-b from-[#0d0d0d] to-[#0a0a0a] relative flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-[#E1C57C] animate-spin" />
      </section>
    );
  }

  if (promotions.length === 0) {
    return null;
  }

  return (
    <section id="promocoes" className="py-24 bg-gradient-to-b from-[#0d0d0d] to-[#0a0a0a] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#C1A54F]/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E5B1C4]/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#E5B1C4] text-sm tracking-[0.3em] uppercase mb-4 block">
            Ofertas Especiais
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-gold-gradient mb-6">
            Packs & Promoções
          </h2>
          <div className="flex items-center justify-center mb-6">
            <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#C1A54F]" />
            <span className="mx-4 text-[#E1C57C]">✦</span>
            <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#C1A54F]" />
          </div>
          <p className="text-[#bbb5ae] max-w-2xl mx-auto text-lg">
            Aproveite os nossos packs exclusivos e poupe nos seus tratamentos favoritos
          </p>
        </div>

        {/* Promotions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promotions.map((promo) => {
            const IconComponent = iconMap[promo.subtitle] || Gift;
            const isRose = promo.highlight;
            
            return (
              <div
                key={promo.id}
                className={`relative p-6 rounded-sm transition-all duration-300 group cursor-pointer
                  ${promo.highlight 
                    ? 'bg-gradient-to-br from-[#E5B1C4]/10 to-[#C1A54F]/5 border-2 border-[#E5B1C4]/30 hover:border-[#E5B1C4]/60' 
                    : 'bg-[#111111] border border-[#C1A54F]/10 hover:border-[#C1A54F]/40'
                  }
                  hover:transform hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20`}
                onClick={scrollToBooking}
              >
                {/* Discount Badge */}
                <div className={`absolute -top-3 right-4 px-3 py-1 text-xs font-medium tracking-wide
                  ${isRose 
                    ? 'bg-gradient-to-r from-[#E5B1C4] to-[#FADCD9] text-[#0a0a0a]' 
                    : 'bg-gradient-to-r from-[#C1A54F] to-[#E1C57C] text-[#0a0a0a]'
                  }`}>
                  {promo.discount}
                </div>

                {/* Icon */}
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4
                  ${isRose 
                    ? 'bg-[#E5B1C4]/10 border border-[#E5B1C4]/30 group-hover:bg-[#E5B1C4]/20' 
                    : 'bg-[#C1A54F]/10 border border-[#C1A54F]/30 group-hover:bg-[#C1A54F]/20'
                  } transition-all`}>
                  <IconComponent className={isRose ? 'text-[#E5B1C4]' : 'text-[#E1C57C]'} size={24} />
                </div>

                {/* Content */}
                <div className="mb-4">
                  <p className={`text-xs uppercase tracking-wider mb-1 ${isRose ? 'text-[#E5B1C4]' : 'text-[#E1C57C]'}`}>
                    {promo.subtitle}
                  </p>
                  <h3 className="font-display text-2xl text-[#f5f0e8] mb-2">
                    {promo.title}
                  </h3>
                  <p className="text-[#bbb5ae] text-sm leading-relaxed">
                    {promo.description}
                  </p>
                </div>

                {/* Price */}
                <div className="pt-4 border-t border-[#C1A54F]/10">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${isRose ? 'text-[#E5B1C4]' : 'text-[#E1C57C]'}`}>
                      {promo.price}
                    </span>
                    <span className="text-[#f5f0e8] text-sm flex items-center group-hover:text-[#E1C57C] transition-colors">
                      Marcar
                      <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>

                {/* Highlight effect for special promos */}
                {promo.highlight && (
                  <div className="absolute inset-0 rounded-sm bg-gradient-to-br from-[#E5B1C4]/5 to-transparent pointer-events-none" />
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-[#bbb5ae] mb-6">
            Tem dúvidas sobre qual pack é ideal para si? Contacte-nos!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={scrollToBooking}
              className="btn-gold px-10 py-4 rounded-none font-medium tracking-widest text-sm"
            >
              MARCAR CONSULTA GRATUITA
            </Button>
            <a
              href={`https://wa.me/351936694088?text=Olá! Gostaria de saber mais sobre os packs e promoções da Shape & Shine.`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                variant="outline"
                className="btn-outline-gold px-10 py-4 rounded-none font-medium tracking-widest text-sm"
              >
                PERGUNTAR VIA WHATSAPP
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Promotions;
